import { api } from './client'
import { request as adminRequest, token, redirectExpiredAdminSession } from './admin'

/* ── Public (landing) ── */

/** Yazının çeviri grubundaki bir dil versiyonu (hreflang + dil değiştirici için). */
export interface BlogTranslationRef {
  id: string
  language: string
  slug: string
  status: 'draft' | 'published'
  viewCount: number
}

export interface BlogPostSummary {
  id: string
  slug: string
  title: string
  excerpt: string
  coverImageUrl: string | null
  tags: string[]
  language: string
  publishedAt: string | null
  viewCount: number
  readingMinutes: number
  authorName: string
  translations?: BlogTranslationRef[]
}

export interface BlogPostDetail extends BlogPostSummary {
  content: string
  updatedAt: string | null
  metaTitle: string | null
  metaDescription: string | null
}

export interface BlogPostListResult {
  page: number
  pageSize: number
  total: number
  items: BlogPostSummary[]
}

export interface BlogTagCount {
  tag: string
  count: number
}

export const blogApi = {
  list: (params: { tag?: string; language?: string; search?: string; page?: number; pageSize?: number; sort?: 'recent' | 'popular' } = {}) => {
    const query = new URLSearchParams()
    if (params.tag) query.set('tag', params.tag)
    if (params.language) query.set('language', params.language)
    if (params.search) query.set('search', params.search)
    if (params.page) query.set('page', String(params.page))
    if (params.pageSize) query.set('pageSize', String(params.pageSize))
    if (params.sort) query.set('sort', params.sort)
    const suffix = query.toString()
    return api.get<BlogPostListResult>(`/blog/posts${suffix ? `?${suffix}` : ''}`)
  },

  getBySlug: (slug: string) =>
    api.get<BlogPostDetail>(`/blog/posts/${encodeURIComponent(slug)}`),

  registerView: (slug: string) =>
    api.post<{ ok: boolean }>(`/blog/posts/${encodeURIComponent(slug)}/view`),

  tags: (language?: string) =>
    api.get<BlogTagCount[]>(`/blog/tags${language ? `?language=${encodeURIComponent(language)}` : ''}`),
}

/* ── Platform admin ── */

export interface AdminBlogPost extends BlogPostSummary {
  status: 'draft' | 'published'
  createdAt: string
  updatedAt: string | null
  groupViewCount?: number
  translationGroupId?: string
}

export interface AdminBlogPostDetail extends AdminBlogPost {
  content: string
  metaTitle: string | null
  metaDescription: string | null
}

export interface AdminBlogPostListResult {
  page: number
  pageSize: number
  total: number
  items: AdminBlogPost[]
}

export interface BlogPostPayload {
  slug: string
  title: string
  excerpt: string
  content: string
  coverImageUrl?: string | null
  tags?: string[]
  language: string
  readingMinutes?: number | null
  metaTitle?: string | null
  metaDescription?: string | null
  authorName?: string | null
  /** Doluysa yazı bu grubun yeni dil çevirisi olarak eklenir. */
  translationGroupId?: string | null
}

// Admin yüzeyi genel `api` client'ını KULLANMAZ: o, uygulama kullanıcısı
// (crm-auth) token'ına bakar ve 401'de /login'e atar. Admin istekleri admin.ts'in
// platform-admin kimlikli request'inden gider (401'de /admin/login'e yönlendirir).
export const blogAdminApi = {
  list: (params: { search?: string; status?: string; language?: string; page?: number; pageSize?: number } = {}) => {
    const query = new URLSearchParams()
    if (params.search) query.set('search', params.search)
    if (params.status) query.set('status', params.status)
    if (params.language) query.set('language', params.language)
    if (params.page) query.set('page', String(params.page))
    if (params.pageSize) query.set('pageSize', String(params.pageSize))
    const suffix = query.toString()
    return adminRequest<AdminBlogPostListResult>(`/blog-posts${suffix ? `?${suffix}` : ''}`)
  },

  getById: (id: string) =>
    adminRequest<AdminBlogPostDetail>(`/blog-posts/${id}`),

  create: (payload: BlogPostPayload & { status: 'draft' | 'published' }) =>
    adminRequest<{ id: string; slug: string; status: string }>('/blog-posts', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  update: (id: string, payload: BlogPostPayload) =>
    adminRequest<{ id: string; slug: string }>(`/blog-posts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  setStatus: (id: string, status: 'draft' | 'published') =>
    adminRequest<{ id: string; status: string }>(`/blog-posts/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),

  remove: (id: string) =>
    adminRequest<void>(`/blog-posts/${id}`, { method: 'DELETE' }),

  uploadCover: async (file: File) => {
    const form = new FormData()
    form.append('file', file)
    // Multipart'ta JSON Content-Type set edilemez, bu yüzden upload ham fetch ile
    // gider; kimlik yine platform admin token'ından gelir.
    const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/admin/blog-posts/upload`, {
      method: 'POST',
      headers: token() ? { Authorization: `Bearer ${token()}` } : undefined,
      body: form,
    })
    redirectExpiredAdminSession(response)
    if (!response.ok) throw new Error('Kapak görseli yüklenemedi.')
    // Zarf casing'i ortama göre değişebiliyor (Data/data) — ikisini de dene.
    const envelope = await response.json().catch(() => null) as { Data?: { url?: string }; data?: { url?: string }; url?: string } | null
    const url = envelope?.Data?.url ?? envelope?.data?.url ?? envelope?.url
    if (!url) throw new Error('Kapak görseli yüklendi ama URL okunamadı; tekrar deneyin.')
    return { url }
  },
}
